import React, { useState, useEffect, useCallback } from 'react';
import SmartTable, { SmartTableColumn } from '../../../shared/components/SmartTable';
import SmartTableButtons from '../../../shared/components/SmartTableButtons';
import LayoutWrapper from '../../../shared/components/LayoutWrapper';
import SearchableDropdown from '../../../shared/components/SearchableDropdown';
import { useAuth } from '../../../shared/hooks/useAuth';
import api from '../../../shared/utils/api';
import ErrorPopup from '../../../shared/components/ErrorPopup';
import WarningPopup from '../../../shared/components/WarningPopup';

interface LedgerRecord {
  Oid: string;
  CustomerOid: string;
  Date: string;
  Description: string | null;
  AccountId: string | null;
  OffsetAccountId: string | null;
  AmountDebit: number | null;
  AmountCredit: number | null;
  BusinessCustomerOid: string | null;
  CashflowCategoryOid: string | null;
  CreateDate: string;
  ReceiptId: string | null;
  Number: number | null;
  LedgerTemplateReceiptItemId: string | null;
  LedgerTemplateBankStatementItemId: string | null;
  LedgerTemplatePayrollId: string | null;
  YearType: number;
  PeriodType: number;
  DayType: number;
  LedgerYearId: string | null;
  JournalId: string | null;
  SequenceNumber: number | null;
  AccountPeriodCloseId: string | null;
  SalaryId: string | null;
  AccountCode: string | null;
  AccountName: string | null;
  OffsetAccountCode: string | null;
  OffsetAccountName: string | null;
  CashflowCategoryName: string | null;
}

interface CustomerOption {
  Oid: string;
  CustomerID: string;
  CustomerName: string;
}

const formatNumber = (value: any) => {
  if (value === null || value === undefined) return '-';
  const num = Number(value);
  if (Number.isNaN(num)) return '-';
  return num.toLocaleString('mn-MN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDate = (value: any) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toISOString().slice(0, 10);
};

const Ledger: React.FC = () => {
  const { userAuth } = useAuth();
  const [ledgerData, setLedgerData] = useState<LedgerRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showWarningAlert, setShowWarningAlert] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  
  // Filter state
  const [selectedCustomerOid, setSelectedCustomerOid] = useState<string>('');
  const [selectedYeartype, setSelectedYeartype] = useState<string>('2025');
  const [selectedPeriodType, setSelectedPeriodType] = useState<string>('');
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [selectedOffsetAccountId, setSelectedOffsetAccountId] = useState<string>('');
  
  // Customer list state
  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [accounts, setAccounts] = useState<Array<{ Oid: string; Code: string; Name: string }>>([]);
  const [showColumnSearch, setShowColumnSearch] = useState(false);
  const [showColumnSettings, setShowColumnSettings] = useState(false);

  // Fetch customers list for dropdown
  const fetchCustomers = useCallback(async () => {
    try {
      const accountantOid = userAuth.accountantOid || localStorage.getItem('accountantOid');
      
      if (!accountantOid) {
        return;
      }

      const queryParams = new URLSearchParams({
        accountantOid: accountantOid
      });
      const apiUrl = `/api/customerslist?${queryParams.toString()}`;
      
      const result = await api.get(apiUrl);
      
      if (result && result.success && result.data) {
        const customerOptions: CustomerOption[] = result.data.map((customer: any) => ({
          Oid: customer.Oid,
          CustomerID: customer.CustomerID,
          CustomerName: customer.CustomerName
        }));
        setCustomers(customerOptions);
      }
    } catch (error) {
      console.error('❌ Error fetching customers:', error);
    }
  }, [userAuth.accountantOid]);

  // Fetch accounts list
  const fetchAccounts = useCallback(async () => {
    try {
      const result = await api.get('/api/account?isActive=true');
      if (result?.success && result?.data) {
        setAccounts(result.data.map((item: any) => ({
          Oid: item.Oid,
          Code: item.Code || '',
          Name: item.Name || ''
        })));
      }
    } catch (error) {
      console.error('❌ Error fetching accounts:', error);
    }
  }, []);

  // Fetch Ledger data
  const fetchLedger = useCallback(async () => {
    try {
      setLoading(true);
      
      if (!selectedCustomerOid || !selectedYeartype) {
        setWarningMessage('Харилцагч болон жил сонгоно уу');
        setShowWarningAlert(true);
        setLedgerData([]);
        return;
      }
      
      const queryParams = new URLSearchParams({
        CustomerOid: selectedCustomerOid,
        yeartype: selectedYeartype
      });
      
      // Add PeriodType if selected
      if (selectedPeriodType && selectedPeriodType !== '') {
        queryParams.append('PeriodType', selectedPeriodType);
      }
      
      // Add AccountId if selected
      if (selectedAccountId && selectedAccountId !== '') {
        queryParams.append('AccountId', selectedAccountId);
      }
      
      // Add OffsetAccountId if selected
      if (selectedOffsetAccountId && selectedOffsetAccountId !== '') {
        queryParams.append('OffsetAccountId', selectedOffsetAccountId);
      }
      
      const apiUrl = `/api/ledger?${queryParams.toString()}`;
      
      console.log('🌐 API URL:', apiUrl);
      
      const result = await api.get(apiUrl);
      
      if (result && result.success && result.data) {
        console.log(`✅ Fetched ${result.data.length} ledger records`);
        
        setLedgerData(result.data);
        
        // Show warning if no data found
        if (result.data.length === 0) {
          setWarningMessage('Ledger мэдээлэл олдсонгүй');
          setShowWarningAlert(true);
        } else {
          setShowWarningAlert(false);
        }
      } else {
        setErrorMessage('Ledger мэдээлэл авахад алдаа гарлаа');
        setShowErrorPopup(true);
        setLedgerData([]);
      }
    } catch (error: any) {
      console.error('❌ Error fetching Ledger:', error);
      console.error('❌ Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      
      let errorMessage = 'Ledger мэдээлэл авахад алдаа гарлаа';
      
      if (error.response?.status === 404) {
        errorMessage = 'API endpoint олдсонгүй. Сервертэй холбогдож чадсангүй.';
        setWarningMessage('Ledger мэдээлэл олдсонгүй');
        setShowWarningAlert(true);
      } else if (error.response?.status === 401) {
        errorMessage = 'Нэвтрэх эрх шаардлагатай. Дахин нэвтэрнэ үү.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Серверийн алдаа. Дахин оролдоно уу.';
      } else if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
        errorMessage = 'Сүлжээний алдаа. Сервер ажиллаж байгаа эсэхийг шалгана уу.';
      }
      
      setErrorMessage(errorMessage);
      setShowErrorPopup(true);
      setLedgerData([]);
    } finally {
      setLoading(false);
    }
  }, [selectedCustomerOid, selectedYeartype, selectedPeriodType, selectedAccountId, selectedOffsetAccountId]);

  // Button handlers
  const handleToggleSearch = () => {
    setShowColumnSearch(prev => !prev);
  };

  const handleColumnSettings = () => {
    setShowColumnSettings(prev => !prev);
  };

  const handleRefresh = () => {
    console.log('🔄 Сэргээх товч дарагдаж, өгөгдөл дахин ачаалж байна...');
    fetchLedger();
  };

  // Calculate footer data with aggregations
  const calculateFooterData = (): any => {
    if (ledgerData.length === 0) {
      return null;
    }

    const sums = {
      amountDebit: 0,
      amountCredit: 0,
    };

    ledgerData.forEach((row) => {
      if (row.AmountDebit !== null && row.AmountDebit !== undefined && !isNaN(row.AmountDebit)) {
        sums.amountDebit += parseFloat(row.AmountDebit.toString());
      }
      if (row.AmountCredit !== null && row.AmountCredit !== undefined && !isNaN(row.AmountCredit)) {
        sums.amountCredit += parseFloat(row.AmountCredit.toString());
      }
    });

    return {
      Date: `Нийт: ${ledgerData.length} мөр`,
      Number: '',
      Description: 'Нийт дүн',
      AccountName: '',
      OffsetAccountName: '',
      CashflowCategoryName: '',
      AmountDebit: sums.amountDebit,
      AmountCredit: sums.amountCredit,
      PeriodType: '',
      DayType: '',
    };
  };

  // Create columns for Ledger data
  const getColumns = (): SmartTableColumn[] => [
    { 
      key: 'Date', 
      label: 'Огноо',
      width: 120,
      render: (value: any, row: any, isFooter?: boolean) => {
        if (isFooter && typeof value === 'string' && value.startsWith('Нийт:')) {
          return <div style={{ fontWeight: 'bold', color: '#1F2937' }}>{value}</div>;
        }
        return formatDate(value);
      }
    },
    { 
      key: 'Number', 
      label: 'Дугаар',
      width: 100,
      render: (value: any, row: any, isFooter?: boolean) => {
        if (isFooter) return '-';
        return value || '-';
      }
    },
    { 
      key: 'Description', 
      label: 'Тайлбар',
      width: 200,
      render: (value: any, row: any, isFooter?: boolean) => {
        if (isFooter) return '-';
        return value || '-';
      }
    },
    { 
      key: 'AccountName', 
      label: 'Данс',
      width: 250,
      render: (value: any, row: any, isFooter?: boolean) => {
        if (isFooter) return '-';
        const code = row.AccountCode || '';
        const name = value || '';
        if (code && name) {
          return `${code} - ${name}`;
        }
        return name || code || '-';
      }
    },
    { 
      key: 'OffsetAccountName', 
      label: 'Эсрэг данс',
      width: 250,
      render: (value: any, row: any, isFooter?: boolean) => {
        if (isFooter) return '-';
        const code = row.OffsetAccountCode || '';
        const name = value || '';
        if (code && name) {
          return `${code} - ${name}`;
        }
        return name || code || '-';
      }
    },
    { 
      key: 'CashflowCategoryName', 
      label: 'Мөнгөн гүйлгээний ангилал',
      width: 200,
      render: (value: any, row: any, isFooter?: boolean) => {
        if (isFooter) return '-';
        return value || '-';
      }
    },
    { 
      key: 'AmountDebit', 
      label: 'Дебет',
      width: 150,
      render: (value: any, row: any, isFooter?: boolean) => {
        if (value === null || value === undefined) return '-';
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (isNaN(numValue)) return '-';
        const formatted = formatNumber(numValue);
        if (isFooter) {
          return <div style={{ fontWeight: 'bold', color: '#1F2937' }}>{formatted}</div>;
        }
        return formatted;
      }
    },
    { 
      key: 'AmountCredit', 
      label: 'Кредит',
      width: 150,
      render: (value: any, row: any, isFooter?: boolean) => {
        if (value === null || value === undefined) return '-';
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (isNaN(numValue)) return '-';
        const formatted = formatNumber(numValue);
        if (isFooter) {
          return <div style={{ fontWeight: 'bold', color: '#1F2937' }}>{formatted}</div>;
        }
        return formatted;
      }
    },
    { 
      key: 'PeriodType', 
      label: 'Сар',
      width: 80,
      render: (value: any, row: any, isFooter?: boolean) => {
        if (isFooter) return '-';
        return value || '-';
      }
    },
    { 
      key: 'DayType', 
      label: 'Өдөр',
      width: 80,
      render: (value: any, row: any, isFooter?: boolean) => {
        if (isFooter) return '-';
        return value || '-';
      }
    },
  ];

  useEffect(() => {
    // Fetch customers list and accounts list on mount
    fetchCustomers();
    fetchAccounts();
  }, [fetchCustomers, fetchAccounts]);

  useEffect(() => {
    // Fetch ledger data when filters change
    if (selectedCustomerOid && selectedYeartype) {
      fetchLedger();
    }
  }, [fetchLedger, selectedCustomerOid, selectedYeartype, selectedPeriodType, selectedAccountId, selectedOffsetAccountId]);

  // Customer dropdown options
  const customerOptions = customers.map(customer => ({
    value: customer.Oid,
    label: `${customer.CustomerName || ''} (${customer.CustomerID || ''})`
  }));

  // Selected customer name for display
  const selectedCustomerName = customers.find(c => c.Oid === selectedCustomerOid) 
    ? `${customers.find(c => c.Oid === selectedCustomerOid)?.CustomerName || ''} (${customers.find(c => c.Oid === selectedCustomerOid)?.CustomerID || ''})`
    : '';

  return (
    <LayoutWrapper userAuth={userAuth}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Журнал</h1>
          
          {/* Action Buttons */}
          <SmartTableButtons
            showColumnSearch={showColumnSearch}
            onToggleSearch={handleToggleSearch}
            onColumnSettings={handleColumnSettings}
            onRefresh={handleRefresh}
            addable={false}
          />
        </div>

        {/* Filters */}
        <div className="mb-4 flex items-center gap-4">
          <div className="bg-white rounded-lg shadow p-4 min-w-[150px]">
            <div className="text-sm text-gray-600">Нийт бичлэг</div>
            <div className="text-2xl font-bold text-gray-800">
              {ledgerData.length}
            </div>
          </div>
          <div className="flex-1 flex gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Харилцагч сонгох
              </label>
              <SearchableDropdown
                options={customerOptions}
                value={selectedCustomerOid}
                onChange={(value) => setSelectedCustomerOid(value)}
                valueLabel={selectedCustomerName}
                placeholder="Харилцагч сонгох"
                className="w-full"
                enableAutoComplete={true}
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Жил (yeartype)
              </label>
              <input
                type="text"
                value={selectedYeartype}
                onChange={(e) => setSelectedYeartype(e.target.value)}
                placeholder="Жил оруулах (жишээ: 2025)"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={loading}
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Сар (PeriodType) - Заавал биш
              </label>
              <input
                type="text"
                value={selectedPeriodType}
                onChange={(e) => setSelectedPeriodType(e.target.value)}
                placeholder="Сар оруулах (жишээ: 1)"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={loading}
              />
            </div>
          </div>
        </div>

        {/* Account Filters */}
        <div className="mb-4 flex items-center gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Данс сонгох (AccountId) - Заавал биш
            </label>
            <SearchableDropdown
              options={[
                { value: '', label: 'Бүгд' },
                ...accounts.map(acc => ({
                  value: acc.Oid,
                  label: `${acc.Code || ''} - ${acc.Name || ''}`
                }))
              ]}
              value={selectedAccountId}
              onChange={(value) => setSelectedAccountId(value)}
              valueLabel={
                !selectedAccountId
                  ? 'Бүгд'
                  : (accounts.find(a => a.Oid === selectedAccountId)
                      ? `${accounts.find(a => a.Oid === selectedAccountId)?.Code || ''} - ${accounts.find(a => a.Oid === selectedAccountId)?.Name || ''}`
                      : '')
              }
              placeholder="Данс сонгох"
              className="w-full"
              enableAutoComplete={true}
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Эсрэг данс сонгох (OffsetAccountId) - Заавал биш
            </label>
            <SearchableDropdown
              options={[
                { value: '', label: 'Бүгд' },
                ...accounts.map(acc => ({
                  value: acc.Oid,
                  label: `${acc.Code || ''} - ${acc.Name || ''}`
                }))
              ]}
              value={selectedOffsetAccountId}
              onChange={(value) => setSelectedOffsetAccountId(value)}
              valueLabel={
                !selectedOffsetAccountId
                  ? 'Бүгд'
                  : (accounts.find(a => a.Oid === selectedOffsetAccountId)
                      ? `${accounts.find(a => a.Oid === selectedOffsetAccountId)?.Code || ''} - ${accounts.find(a => a.Oid === selectedOffsetAccountId)?.Name || ''}`
                      : '')
              }
              placeholder="Эсрэг данс сонгох"
              className="w-full"
              enableAutoComplete={true}
            />
          </div>
        </div>

        {loading && (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Уншиж байна...</p>
          </div>
        )}

        {!loading && (
          <SmartTable
            data={ledgerData}
            columns={getColumns()}
            fetchData={fetchLedger}
            settingsKey="ledger-table"
            editable={false}
            addable={false}
            rowKey="Oid"
            showColumnSearch={showColumnSearch}
            showColumnSettings={showColumnSettings}
            footerData={calculateFooterData()}
          />
        )}

        <ErrorPopup
          isVisible={showErrorPopup}
          message={errorMessage}
          onClose={() => setShowErrorPopup(false)}
        />
        
        <WarningPopup
          isVisible={showWarningAlert}
          message={warningMessage}
          onClose={() => setShowWarningAlert(false)}
          duration={5000}
        />
      </div>
    </LayoutWrapper>
  );
};

export default Ledger;